import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'

import apis from '@/services'
import { selectedAccommodationId } from '@/layouts/queries'

export const getTimeFramesTrends = async ({ accommodationId, timeframe }) => {
  const startDate = calculateStartDateFromTimeframe(timeframe)
  const response = await apis.accommodation.fetchTimeFramesTrends({ accommodationId, startDate })
  return response.data
}

export function useTimeFrameTrends(timeframe) {
  const { data, isError, isLoading, isFetching, fetchStatus, isSuccess } = useQuery({
    enabled: !!selectedAccommodationId?.value,
    queryKey: ['timeFrameTrends', selectedAccommodationId, timeframe],
    queryFn: ({ queryKey }) =>
      getTimeFramesTrends({
        accommodationId: queryKey[1],
        timeframe: queryKey[2],
      }),
  })

  const isTimeFrameTrendsLoading = computed(() => isLoading.value && fetchStatus.value !== 'idle')

  return {
    isTimeFrameTrendsFetching: isFetching,
    isTimeFrameTrendsLoading,
    isTimeFrameTrendsSuccess: isSuccess,
    isTimeFrameTrendsError: isError,
    timeFrameTrends: data,
  }
}

export const getVisitorsProfileCountries = async ({ accommodationId, timeframe }) => {
  const startDate = calculateStartDateFromTimeframe(timeframe)
  const response = await apis.accommodation.fetchVisitorsProfileCountries({ accommodationId, startDate })
  return response.data ?? []
}

export function useVisitorsProfileCountries(timeframe) {
  const { data, isError, isLoading, isFetching, fetchStatus, isSuccess } = useQuery({
    enabled: !!selectedAccommodationId?.value,
    queryKey: ['visitorsProfileCountries', selectedAccommodationId, timeframe],
    queryFn: ({ queryKey }) =>
      getVisitorsProfileCountries({
        accommodationId: queryKey[1],
        timeframe: queryKey[2],
      }),
  })
  const isVisitorsProfileCountriesLoading = computed(() => isLoading.value && fetchStatus.value !== 'idle')

  return {
    isVisitorsProfileCountriesFetching: isFetching,
    isVisitorsProfileCountriesLoading,
    isVisitorsProfileCountriesSuccess: isSuccess,
    isVisitorsProfileCountriesError: isError,
    visitorsProfileCountries: data,
  }
}

export const getVisitorsProfileDuration = async ({ accommodationId, timeframe }) => {
  const startDate = calculateStartDateFromTimeframe(timeframe)
  const response = await apis.accommodation.fetchVisitorsProfileDuration({ accommodationId, startDate })
  return response.data
}

export function useVisitorsProfileDuration(timeframe) {
  const { isLoading, data, isFetching, isSuccess, isError, fetchStatus } = useQuery({
    enabled: !!selectedAccommodationId?.value,
    queryKey: ['visitorsProfileDuration', selectedAccommodationId, timeframe],
    queryFn: ({ queryKey }) => getVisitorsProfileDuration({ accommodationId: queryKey[1], timeframe: queryKey[2] }),
  })

  const isVisitorsProfileDurationLoading = computed(() => isLoading.value && fetchStatus.value !== 'idle')

  return {
    isVisitorsProfileDurationFetching: isFetching,
    isVisitorsProfileDurationLoading,
    isVisitorsProfileDurationSuccess: isSuccess,
    isVisitorsProfileDurationError: isError,
    visitorsProfileDuration: data,
  }
}

function calculateStartDateFromTimeframe(timeframe) {
  if (!timeframe) return ''

  const today = new Date()
  const date = today.setDate(today.getDate() - Number(timeframe))
  return new Date(date).toISOString().split('T')[0]
}
